import { createEffect, createSignal, mergeProps, onCleanup, Show, splitProps } from 'solid-js';
import type { JSX } from 'cdui-js';
import { Icon } from 'cdui-js';
import {
  diFiInputMsg,
  filterDiFiInputValue,
  getKindDefaultMaxLength,
  getSuggestedInputMode,
  hasValidationRules,
  validateDiFiInput,
  type DiFiInputKind,
  type DiFiInputRuleOptions,
  type DiFiValidateResult,
} from './rules';
import { normalizeDecimalOnBlur } from './number';
import './difi-input.css';

export type { DiFiInputKind, DiFiInputRuleOptions, DiFiValidateResult };
export {
  diFiInputMsg,
  filterDiFiInputValue,
  validateDiFiInput,
  hasValidationRules,
} from './rules';

/** 预留默认值，可按设计稿调整 */
const DEFAULT_MAX_LENGTH = 100;
const DEFAULT_PLACEHOLDER = '';
const DEFAULT_CLEAR_TEXT = '清空';
const DEFAULT_DEBOUNCE_MS = 0;
/** 右侧图标 / 字数距外壳右边缘，统一 12px */
const RIGHT_EDGE_GAP = 12;
const CLEAR_ICON_SIZE = 16;

const rowStyle: JSX.CSSProperties = {
  'min-width': 0,
};

const shellBaseStyle: JSX.CSSProperties = {
  position: 'relative',
  'box-sizing': 'border-box',
  width: '100%',
  'min-width': 0,
  display: 'flex',
  'flex-direction': 'row',
  'align-items': 'center',
  gap: '8px',
};

const DEFAULT_SHELL_CLASS = 'f-input';

const clearBtnStyle: JSX.CSSProperties = {
  position: 'absolute',
  top: '50%',
  right: `${RIGHT_EDGE_GAP}px`,
  transform: 'translateY(-50%)',
  'z-index': 100,
  border: 'none',
  background: 'transparent',
  padding: 0,
  margin: 0,
  'border-radius': '50%',
  cursor: 'pointer',
};

const countStyle: JSX.CSSProperties = {
  position: 'absolute',
  top: '50%',
  right: `${RIGHT_EDGE_GAP}px`,
  transform: 'translateY(-50%)',
  'z-index': 0,
  'line-height': 1,
  'pointer-events': 'none',
  'font-size': '10px',
};

export type DiFiInputProps = {
  value?: string;
  defaultValue?: string;
  /**
   * 最大字符数（Unicode 标量）；未传时优先用 inputKind 默认上限，否则 DEFAULT_MAX_LENGTH；
   * 0 表示不限制且不展示计数。
   */
  maxLength?: number;
  placeholder?: string;
  /** 传 `textarea` 时渲染多行输入，其余走原生 input type */
  type?: string;
  /** `type="textarea"` 时的行数，默认 3 */
  rows?: number;
  inputMode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';
  disabled?: boolean;
  readonly?: boolean;
  showClear?: boolean;
  showCount?: boolean;
  clearText?: string;
  onUpdateValue?: (value: string) => void;
  debounceMs?: number;
  onClear?: () => void;
  class?: string;
  style?: JSX.CSSProperties;
  wrapperClass?: string;
  rowClass?: string;
  /** 输入外壳 class，默认 `f-input`；开户表单等场景可传 `f-input-2` */
  shellClass?: string;
  inputClass?: string;
  inputStyle?: JSX.CSSProperties;
  /** 整个组件上方自定义区域 */
  headerRender?: () => JSX.Element;
  /** 整个组件下方自定义区域 */
  footerRender?: () => JSX.Element;
  /** 边框内侧左侧（与原生 input 同一行） */
  prefixRender?: () => JSX.Element;
  /** 边框内侧右侧（在字数/清空占位左侧，与 input 同一行） */
  suffixRender?: () => JSX.Element;

  /**
   * 内置输入类型：身份证（大陆/港/澳）、手机、座机、国际电话、邮箱（email/mail）、
   * 纯英文（english）、纯数字（number/digit）、小数（decimal）、金额（amount）、资金（fund）、
   * 数字+英文（number-en）、银行账户（bank-account，含连字符 `-`）等。
   * 与 `pattern` 同时存在时，`pattern` 优先用于过滤。
   */
  inputKind?: DiFiInputKind;
  /**
   * 小数位数（仅 decimal / amount / fund 生效）：
   * - decimal：未传不限制
   * - amount / fund：未传默认 2
   */
  decimalPlaces?: number;
  /** 自定义输入过滤正则（整串匹配）；优先于 inputKind */
  pattern?: RegExp | string;
  /** 自定义完整值校验正则；优先于 inputKind 默认校验 */
  validatePattern?: RegExp | string;
  /** 手机号区号，如 `+86`；影响 mobile 位数与校验 */
  areaCode?: string;
  /** 空值是否视为校验通过，默认 true */
  allowEmpty?: boolean;
  /** 失焦时自动校验，默认在存在校验规则时为 true */
  validateOnBlur?: boolean;
  /** 校验结果回调（blur 或手动 triggerValidate） */
  onValidate?: (result: DiFiValidateResult) => void;
  onBlur?: JSX.EventHandlerUnion<HTMLInputElement, FocusEvent>;
};

const defaultProps: Required<
  Pick<
    DiFiInputProps,
    | 'defaultValue'
    | 'placeholder'
    | 'type'
    | 'disabled'
    | 'readonly'
    | 'showClear'
    | 'showCount'
    | 'clearText'
    | 'debounceMs'
    | 'shellClass'
    | 'allowEmpty'
  >
> = {
  defaultValue: '',
  placeholder: DEFAULT_PLACEHOLDER,
  type: 'text',
  disabled: false,
  readonly: false,
  showClear: true,
  showCount: false,
  clearText: DEFAULT_CLEAR_TEXT,
  debounceMs: DEFAULT_DEBOUNCE_MS,
  shellClass: DEFAULT_SHELL_CLASS,
  allowEmpty: true,
};

/** 将受控 value 规范为 string，避免接口 null/undefined 导致 [...value] 抛错 */
function toInputString(value: unknown): string {
  return value == null ? '' : String(value);
}

function clampByMaxLength(value: string, max: number): string {
  if (max <= 0) return value;
  const chars = [...value];
  if (chars.length <= max) return value;
  return chars.slice(0, max).join('');
}

function charCount(value: string): number {
  return [...toInputString(value)].length;
}

/**
 * 单行输入：清空、字数上限、格式过滤/校验、onUpdateValue / onClear；可选头部/尾部/前缀/后缀插槽。
 */
export const DiFiInput = (props: DiFiInputProps) => {
  const merged = mergeProps(defaultProps, props);
  const [local, inputRest] = splitProps(merged, [
    'value',
    'defaultValue',
    'maxLength',
    'onUpdateValue',
    'debounceMs',
    'onClear',
    'showClear',
    'showCount',
    'clearText',
    'wrapperClass',
    'rowClass',
    'shellClass',
    'inputClass',
    'inputStyle',
    'class',
    'style',
    'placeholder',
    'type',
    'rows',
    'inputMode',
    'disabled',
    'readonly',
    'headerRender',
    'footerRender',
    'prefixRender',
    'suffixRender',
    'inputKind',
    'decimalPlaces',
    'pattern',
    'validatePattern',
    'areaCode',
    'allowEmpty',
    'validateOnBlur',
    'onValidate',
    'onBlur',
  ]);

  const isTextarea = () => local.type === 'textarea';

  const [live, setLive] = createSignal(
    local.value !== undefined ? toInputString(local.value) : toInputString(local.defaultValue),
  );

  /** 中文等 IME 组合输入中：禁止回写受控 value / 过滤，否则会打断组字并成倍插入字符 */
  let composing = false;
  /** english 过滤删字后的提示；合法英文输入后清掉 */
  const [filterHint, setFilterHint] = createSignal('');

  createEffect(() => {
    const v = local.value;
    if (v !== undefined && !composing) {
      setLive(() => toInputString(v));
    }
  });

  let debounceTimer: ReturnType<typeof setTimeout> | undefined;
  const cancelDebounce = () => {
    if (debounceTimer !== undefined) {
      clearTimeout(debounceTimer);
      debounceTimer = undefined;
    }
  };

  onCleanup(() => cancelDebounce());

  const ruleOptions = (): DiFiInputRuleOptions => ({
    inputKind: local.inputKind,
    decimalPlaces: local.decimalPlaces,
    pattern: local.pattern,
    validatePattern: local.validatePattern,
    areaCode: local.areaCode,
    allowEmpty: local.allowEmpty,
  });

  const effectiveMax = () => {
    if (props.maxLength !== undefined) return props.maxLength as number;
    const kindMax = getKindDefaultMaxLength(local.inputKind, local.areaCode);
    if (kindMax !== undefined) return kindMax;
    return DEFAULT_MAX_LENGTH;
  };

  const shouldValidateOnBlur = () => {
    if (local.validateOnBlur !== undefined) return local.validateOnBlur;
    return hasValidationRules(ruleOptions());
  };

  const emitUpdate = (v: string) => {
    local.onUpdateValue?.(v);
  };

  const runValidate = (value = live()): DiFiValidateResult => {
    const result = validateDiFiInput(value, ruleOptions());
    local.onValidate?.(result);
    return result;
  };

  const applyInputValue = (el: HTMLInputElement | HTMLTextAreaElement) => {
    const prev = live();
    const raw = el.value;
    const options = ruleOptions();
    let v = filterDiFiInputValue(raw, options, prev);
    const englishStripped = local.inputKind === 'english' && local.pattern == null && v !== raw;
    v = clampByMaxLength(v, effectiveMax());
    if (v !== el.value) {
      el.value = v;
    }
    setLive(v);
    if (local.inputKind === 'english' && local.pattern == null) {
      if (englishStripped) {
        setFilterHint(diFiInputMsg('English'));
      } else if (validateDiFiInput(v, options).valid) {
        setFilterHint('');
      }
    }

    const wait = local.debounceMs ?? 0;
    if (wait <= 0) {
      emitUpdate(v);
      return;
    }
    cancelDebounce();
    debounceTimer = setTimeout(() => {
      debounceTimer = undefined;
      emitUpdate(v);
    }, wait);
  };

  const onCompositionStart = () => {
    composing = true;
  };

  const onCompositionEnd: JSX.EventHandlerUnion<
    HTMLInputElement | HTMLTextAreaElement,
    CompositionEvent
  > = (e) => {
    composing = false;
    applyInputValue(e.currentTarget);
  };

  const onInput: JSX.EventHandlerUnion<
    HTMLInputElement | HTMLTextAreaElement,
    InputEvent
  > = (e) => {
    // 组字中不回写。isComposing 已结束则清掉标志再提交，避免 compositionend 丢失后按键被永久丢掉
    if (e.isComposing) {
      composing = true;
      return;
    }
    composing = false;
    applyInputValue(e.currentTarget);
  };

  const handleBlur = (e: FocusEvent & { currentTarget: HTMLInputElement | HTMLTextAreaElement }) => {
    if (composing) {
      composing = false;
      applyInputValue(e.currentTarget);
    }
    const kind = local.inputKind;
    if (!isTextarea() && (kind === 'decimal' || kind === 'amount' || kind === 'fund')) {
      const normalized = normalizeDecimalOnBlur(live());
      if (normalized !== live()) {
        setLive(normalized);
        emitUpdate(normalized);
        e.currentTarget.value = normalized;
      }
    }
    if (shouldValidateOnBlur()) {
      runValidate();
    }
    const userBlur = local.onBlur;
    if (typeof userBlur === 'function') {
      userBlur(e as FocusEvent & { currentTarget: HTMLInputElement });
    }
  };

  const handleClear = () => {
    if (local.disabled || local.readonly) return;
    cancelDebounce();
    local.onClear?.();
    setLive('');
    setFilterHint('');
    emitUpdate('');
  };

  const showClearBtn = () =>
    local.showClear && !local.disabled && !local.readonly && charCount(live()) > 0;

  const showCounter = () => local.showCount && effectiveMax() > 0;

  const isFInput2 = () =>
    (local.shellClass || DEFAULT_SHELL_CLASS).split(/\s+/).includes('f-input-2');

  /** 外壳右侧为字数 / 清空预留；两者同时出现时字数在左、清空贴最右 */
  const shellStyle = (): JSX.CSSProperties => {
    const needClear = showClearBtn();
    const needCount = showCounter();
    const basePad = RIGHT_EDGE_GAP;
    let padRight = basePad;
    if (needCount && needClear) {
      // 字数左移 + 清空图标(16) + 右缘 12 + 与文字间距
      padRight = RIGHT_EDGE_GAP + CLEAR_ICON_SIZE + 12 + 40;
    } else if (needClear) {
      padRight = RIGHT_EDGE_GAP + CLEAR_ICON_SIZE + 20;
    } else if (needCount) {
      padRight = RIGHT_EDGE_GAP + 32;
    }
    if (isTextarea()) {
      return {
        ...shellBaseStyle,
        'align-items': 'flex-start',
        height: 'auto',
        'min-height': '96px',
        'padding-top': `${basePad}px`,
        'padding-bottom': `${basePad}px`,
        'padding-left': `${basePad}px`,
        'padding-right': `${padRight}px`,
      };
    }
    // f-input-2 的 height/padding 由 CSS 控制，避免内联样式压过 48px 灰底样式
    return {
      ...shellBaseStyle,
      ...(isFInput2()
        ? {}
        : {
            height: '36px',
            'padding-left': `${basePad}px`,
          }),
      'padding-right': `${padRight}px`,
    };
  };

  const inputFieldStyle = (): JSX.CSSProperties => ({
    flex: '1 1 0%',
    'min-width': 0,
    width: '100%',
    height: isTextarea() ? 'auto' : '100%',
    'min-height': isTextarea() ? '72px' : undefined,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    margin: 0,
    padding: 0,
    resize: isTextarea() ? 'none' : undefined,
    'box-sizing': 'border-box',
    ...local.inputStyle,
  });

  /** 字数与清空同时出现时：清空贴右缘 12，字数在其左侧（图标宽 16 + 间距 12） */
  const countRightPx = () =>
    showClearBtn() && showCounter()
      ? RIGHT_EDGE_GAP + CLEAR_ICON_SIZE + 12
      : RIGHT_EDGE_GAP;

  const resolvedInputMode = () => local.inputMode ?? getSuggestedInputMode(local.inputKind);

  const clearPosStyle = (): JSX.CSSProperties =>
    isTextarea()
      ? { ...clearBtnStyle, top: '12px', transform: 'none' }
      : clearBtnStyle;

  const countPosStyle = (): JSX.CSSProperties =>
    isTextarea()
      ? { ...countStyle, top: 'auto', bottom: '8px', transform: 'none', right: `${countRightPx()}px` }
      : { ...countStyle, right: `${countRightPx()}px` };

  let fieldEl: HTMLInputElement | HTMLTextAreaElement | undefined;

  /**
   * 灰底外壳的上下留白不是 input。点在留白上时补一次聚焦。
   * preventScroll 只挡住浏览器滚外层页面；键盘把输入框顶到可视区仍走 focusin 里的原逻辑。
   * 前缀（区号）和后缀自己处理点击，这里不抢。
   */
  const focusFieldFromShell = (e: MouseEvent) => {
    if (local.disabled || local.readonly) return;
    const target = e.target;
    if (!(target instanceof Element)) return;
    if (target.closest('.difi-input__prefix, .difi-input__suffix, .difi-input__field')) return;
    fieldEl?.focus({ preventScroll: true });
  };

  return (
    <div class={`difi-input flex-column gap-8px width-100 ${local.wrapperClass ?? ''} ${local.class ?? ''}`.trim()} style={local.style}>
      {local.headerRender ? <div class="difi-input__header width-100">{local.headerRender()}</div> : null}

      <div class={`difi-input__row width-100 ${local.rowClass ?? ''}`.trim()} style={rowStyle}>
        <div
          class={`difi-input__shell ${local.shellClass || DEFAULT_SHELL_CLASS} flex-1`.trim()}
          style={shellStyle()}
          onclick={focusFieldFromShell}
        >
          {local.prefixRender ? (
            <div class="difi-input__prefix flex-row align-center flex-shrink-0">{local.prefixRender()}</div>
          ) : null}

          {isTextarea() ? (
            <textarea
              {...(inputRest as any)}
              ref={(el: HTMLTextAreaElement) => {
                fieldEl = el;
              }}
              class={`difi-input__field ${local.inputClass ?? ''}`.trim()}
              style={inputFieldStyle()}
              rows={local.rows ?? 3}
              placeholder={local.placeholder}
              disabled={local.disabled}
              readonly={local.readonly}
              maxlength={effectiveMax() > 0 ? effectiveMax() : undefined}
              value={live()}
              onInput={onInput}
              onCompositionStart={onCompositionStart}
              onCompositionEnd={onCompositionEnd}
              onBlur={handleBlur}
            />
          ) : (
            <input
              {...inputRest}
              ref={(el: HTMLInputElement) => {
                fieldEl = el;
              }}
              class={`difi-input__field ${local.inputClass ?? ''}`.trim()}
              style={inputFieldStyle()}
              type={local.type}
              inputMode={resolvedInputMode()}
              placeholder={local.placeholder}
              disabled={local.disabled}
              readonly={local.readonly}
              maxlength={effectiveMax() > 0 ? effectiveMax() : undefined}
              value={live()}
              onInput={onInput}
              onCompositionStart={onCompositionStart}
              onCompositionEnd={onCompositionEnd}
              onBlur={handleBlur}
            />
          )}

          {local.suffixRender ? (
            <div class="difi-input__suffix flex-row align-center flex-shrink-0">{local.suffixRender()}</div>
          ) : null}

          {showCounter() ? (
            <span class="difi-input__count color-L" style={countPosStyle()}>
              {charCount(live())}/{effectiveMax()}
            </span>
          ) : null}

          {showClearBtn() ? (
            <Icon
              name="common-delete-solid"
              class="icon-s-S"
              style={{
                width: `${CLEAR_ICON_SIZE}px`,
                height: `${CLEAR_ICON_SIZE}px`,
                ...clearPosStyle(),
                // 实心圆灰底 + 白色叉（对应 color-XL / 白）；勿用 icon-c 同色 fill/stroke
                fill: '#a2a7ad',
                stroke: '#FFFFFF',
              }}
              onclick={handleClear}
            />
          ) : null}
        </div>
      </div>

      <Show when={filterHint()}>
        <div class="difi-input__hint color-error font-s-XS">{filterHint()}</div>
      </Show>

      {local.footerRender ? <div class="difi-input__footer width-100">{local.footerRender()}</div> : null}
    </div>
  );
};

/** 供外部在提交时主动校验当前值 */
export function validateDiFiInputValue(
  value: string,
  options: DiFiInputRuleOptions = {},
): DiFiValidateResult {
  return validateDiFiInput(value, options);
}
